    import React, { useMemo, useState, useEffect } from "react";
import { useCountries, Country } from "react-countries";

import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-3.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-3.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import searchIcon from "../../assets/images/svg/country-search.svg";
import globalIcon from "../../assets/images/svg/mab-global.svg";
import dropdownBg from "../../assets/images/svg/mab-bd-dropdown2.svg";
import NextButton from "../common/next-button";

type CountryOption = {
  id: string;
  label: string;
  countryCode?: string; // ISO 3166-1 alpha-2 for ReactCountryFlag
  flagEmoji?: string;
  dialCode?: string;
  tier?: "tier-one" | "tier-two" | "tier-three";
};

const TIER_OPTIONS: CountryOption[] = [
  { id: "tier-one", label: "Tier One Countries" },
  { id: "tier-two", label: "Tier Two Countries" },
  { id: "tier-three", label: "Tier Three Countries" },
  { id: "tier-one-two", label: "Tier One and Two Countries" },
];

const TIER_COUNTRIES: Record<"tier-one" | "tier-two" | "tier-three", string[]> = {
  "tier-one": [
    "Australia",
    "Austria",
    "Belgium",
    "Canada",
    "Denmark",
    "Finland",
    "France",
    "Germany",
    "Ireland",
    "Italy",
    "Japan",
    "Netherlands",
    "New Zealand",
    "Norway",
    "Portugal",
    "Spain",
    "Sweden",
    "Switzerland",
    "United Kingdom",
    "United States",
  ],
  "tier-two": [
    "Argentina",
    "Brazil",
    "Chile",
    "Colombia",
    "Costa Rica",
    "Croatia (Hrvatska)",
    "French Guiana",
    "Hong Kong",
    "Iceland",
    "Israel",
    "South Korea",
    "Latvia",
    "Luxembourg",
    "Malta",
    "Mexico",
    "Monaco",
    "Peru",
    "Poland",
    "South Africa",
    "Turkey",
    "Ukraine",
    "Uruguay",
  ],
  "tier-three": [
    "Albania",
    "Algeria",
    "Andorra",
    "Bahrain",
    "Belarus",
    "Bolivia",
    "Bosnia and Herzegovina",
    "Bulgaria",
    "Cyprus",
    "Czech Republic",
    "Dominican Republic",
    "Ecuador",
    "Egypt",
    "El Salvador",
    "Estonia",
    "Greece",
    "Guatemala",
    "Honduras",
    "Hungary",
    "India",
    "Jordan",
    "Kazakhstan",
    "Kuwait",
    "Lebanon",
    "Liechtenstein",
    "Lithuania",
    "Macedonia",
    "Malaysia",
    "Moldova",
    "Morocco",
    "Nicaragua",
    "Oman",
    "Palestinian Territory Occupied",
    "Panama",
    "Paraguay",
    "Philippines",
    "Qatar",
    "Romania",
    "Saudi Arabia",
    "Serbia",
    "Slovakia",
    "Slovenia",
    "Tunisia",
    "United Arab Emirates",
    "Vietnam",
  ],
};

type CountriesDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onCountryChange?: (value: string | null) => void;
  onNext?: (id: string) => void;
  initialSelectedCountries?: string[]; // Array of ISO country codes for edit mode
  isEditMode?: boolean;
};

const CountriesDropdown: React.FC<CountriesDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onCountryChange,
  onNext,
  initialSelectedCountries,
  isEditMode = false,
}) => {
  const isOpen = expandedId === id;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { countries } = useCountries();

  // Helper function to check if countries belong to a specific tier
  const areCountriesInTier = (countryCodes: string[], tier: "tier-one" | "tier-two" | "tier-three"): boolean => {
    if (!countries) return false;
    const tierCountryNames = TIER_COUNTRIES[tier];
    return countryCodes.every(code => {
      const country = countries.find((c: Country) => c.code === code);
      return country && tierCountryNames.includes(country.name);
    });
  };

  // Initialize selected countries from props (for edit mode)
  useEffect(() => {
    if (initialSelectedCountries && initialSelectedCountries.length > 0 && countries && countries.length > 0) {
      console.log("=== COUNTRY DROPDOWN DEBUG ===");
      console.log("Initial countries:", initialSelectedCountries);

      // Check if it's a tier name (simple string match)
      const tierNameToId: Record<string, string> = {
        "Tier One Countries": "tier-one",
        "Tier Two Countries": "tier-two",
        "Tier Three Countries": "tier-three",
        "Tier One and Two Countries": "tier-one-two",
      };

      const firstItem = initialSelectedCountries[0];

      // If it's a tier name, directly select that tier AND its countries
      if (tierNameToId[firstItem]) {
        const tierId = tierNameToId[firstItem];
        console.log("✓ Tier name detected, selecting tier:", tierId);

        // Get all country IDs for this tier
        const getCountryIdsForTier = (tierId: string): string[] => {
          if (tierId === "tier-one-two") {
            return countries
              .filter((c: Country) =>
                TIER_COUNTRIES["tier-one"].includes(c.name) ||
                TIER_COUNTRIES["tier-two"].includes(c.name)
              )
              .map((c: Country) => c.code);
          } else {
            const tierKey = tierId as "tier-one" | "tier-two" | "tier-three";
            return countries
              .filter((c: Country) => TIER_COUNTRIES[tierKey].includes(c.name))
              .map((c: Country) => c.code);
          }
        };

        const tierCountryIds = getCountryIdsForTier(tierId);
        console.log("✓ Setting tier + countries:", tierId, tierCountryIds.length, "countries");

        // Set both tier ID and all its countries
        setSelectedIds([tierId, ...tierCountryIds]);
        return;
      }

      // Otherwise, it's ISO codes - use the existing logic
      console.log("Initial countries (ISO codes):", initialSelectedCountries);
      console.log("Available countries count:", countries.length);

      // Check if all countries belong to a single tier
      const isAllTierOne = areCountriesInTier(initialSelectedCountries, "tier-one");
      const isAllTierTwo = areCountriesInTier(initialSelectedCountries, "tier-two");
      const isAllTierThree = areCountriesInTier(initialSelectedCountries, "tier-three");

      console.log("Tier detection:");
      console.log("  - All Tier One?", isAllTierOne);
      console.log("  - All Tier Two?", isAllTierTwo);
      console.log("  - All Tier Three?", isAllTierThree);

      if (isAllTierOne) {
        console.log("✓ Setting tier-one + individual countries as selected");
        setSelectedIds(["tier-one", ...initialSelectedCountries]);
      } else if (isAllTierTwo) {
        console.log("✓ Setting tier-two + individual countries as selected");
        setSelectedIds(["tier-two", ...initialSelectedCountries]);
      } else if (isAllTierThree) {
        console.log("✓ Setting tier-three + individual countries as selected");
        setSelectedIds(["tier-three", ...initialSelectedCountries]);
      } else {
        // Check if it's a combination of tier-one and tier-two
        const hasTierOne = initialSelectedCountries.some(code => {
          const country = countries.find((c: Country) => c.code === code);
          return country && TIER_COUNTRIES["tier-one"].includes(country.name);
        });
        const hasTierTwo = initialSelectedCountries.some(code => {
          const country = countries.find((c: Country) => c.code === code);
          return country && TIER_COUNTRIES["tier-two"].includes(country.name);
        });
        const hasTierThree = initialSelectedCountries.some(code => {
          const country = countries.find((c: Country) => c.code === code);
          return country && TIER_COUNTRIES["tier-three"].includes(country.name);
        });

        console.log("Mixed tiers detection:");
        console.log("  - Has Tier One?", hasTierOne);
        console.log("  - Has Tier Two?", hasTierTwo);
        console.log("  - Has Tier Three?", hasTierThree);

        if (hasTierOne && hasTierTwo && !hasTierThree) {
          console.log("✓ Setting tier-one-two + individual countries as selected");
          setSelectedIds(["tier-one-two", ...initialSelectedCountries]);
        } else {
          // Select individual countries only
          console.log("✓ Setting individual countries as selected");
          setSelectedIds(initialSelectedCountries);
        }
      }
      console.log("===========================");
    }
  }, [initialSelectedCountries, countries]);

  const getTierForCountry = (name: string): "tier-one" | "tier-two" | "tier-three" | undefined => {
    if (TIER_COUNTRIES["tier-one"].includes(name)) return "tier-one";
    if (TIER_COUNTRIES["tier-two"].includes(name)) return "tier-two";
    if (TIER_COUNTRIES["tier-three"].includes(name)) return "tier-three";
    return undefined;
  };

  const isTierId = (id: string) =>
    id === "tier-one" || id === "tier-two" || id === "tier-three" || id === "tier-one-two";

  const countryOptions: CountryOption[] = useMemo(
    () =>
      [
        ...TIER_OPTIONS,
        ...(countries || []).map((c: Country) => ({
          id: c.code,
          label: c.name,
          countryCode: c.code,
          flagEmoji: c.flag,
          dialCode: c.dial_code,
          tier: getTierForCountry(c.name),
        })),
      ],
    [countries]
  );

  const filteredOptions = useMemo(
    () =>
      countryOptions.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [countryOptions, searchTerm]
  );

  const selectedOptions: CountryOption[] = useMemo(
    () => countryOptions.filter((o) => selectedIds.includes(o.id)),
    [countryOptions, selectedIds]
  );

  const selectedCountryOptions = useMemo(
    () => selectedOptions.filter((o) => !isTierId(o.id)),
    [selectedOptions]
  );

  const setSelected = (ids: string[]) => {
    setSelectedIds(ids);
    if (onCountryChange) {
      // Check if any tier IDs are selected
      const selectedTierIds: string[] = ids.filter(id => isTierId(id));
      
      // Get all country IDs that belong to selected tiers
      const tierCountryIds: string[] = [];
      selectedTierIds.forEach(tierId => {
        if (tierId === "tier-one-two") {
          // Combine tier-one and tier-two countries
          const countries = countryOptions
            .filter(
              (o) =>
                o.tier === "tier-one" ||
                o.tier === "tier-two" ||
                TIER_COUNTRIES["tier-one"].includes(o.label) ||
                TIER_COUNTRIES["tier-two"].includes(o.label)
            )
            .map((o) => o.id);
          tierCountryIds.push(...countries);
        } else {
          const tierKey = tierId as "tier-one" | "tier-two" | "tier-three";
          const countries = countryOptions
            .filter(
              (o) =>
                o.tier === tierKey ||
                TIER_COUNTRIES[tierKey].includes(o.label)
            )
            .map((o) => o.id);
          tierCountryIds.push(...countries);
        }
      });
      
      // Get tier labels
      const tierLabels = countryOptions
        .filter((o) => selectedTierIds.some(tierId => tierId === o.id))
        .map((o) => o.label);
      
      // Get individual country labels that are NOT part of any selected tier
      const individualCountryLabels = countryOptions
        .filter((o) => 
          ids.includes(o.id) && 
          !isTierId(o.id) && 
          !tierCountryIds.includes(o.id)
        )
        .map((o) => o.label);
      
      // Combine tier labels and individual country labels
      const allLabels = [...tierLabels, ...individualCountryLabels];
      onCountryChange(allLabels.join(", "));
    }
  };

  // Helper function to get all countries for a tier
  const getTierCountries = (tierId: string): string[] => {
    if (tierId === "tier-one-two") {
      // Combine tier-one and tier-two countries
      return countryOptions
        .filter(
          (o) =>
            o.tier === "tier-one" ||
            o.tier === "tier-two" ||
            TIER_COUNTRIES["tier-one"].includes(o.label) ||
            TIER_COUNTRIES["tier-two"].includes(o.label)
        )
        .map((o) => o.id);
    } else {
      const tierKey = tierId as "tier-one" | "tier-two" | "tier-three";
      return countryOptions
        .filter(
          (o) =>
            o.tier === tierKey ||
            TIER_COUNTRIES[tierKey].includes(o.label)
        )
        .map((o) => o.id);
    }
  };

  // Helper function to check if a country ID belongs to any tier
  const isCountryInAnyTier = (countryId: string): boolean => {
    return countryOptions.some(
      (o) => o.id === countryId && o.tier !== undefined
    );
  };

  // Helper function to get all currently selected tier IDs
  const getSelectedTierIds = (): string[] => {
    return selectedIds.filter((id) => isTierId(id));
  };

  const handleSelect = (option: CountryOption) => {
    // Toggle selection
    const isSelected = selectedIds.includes(option.id);

    // Tier handling
    const isTier =
      option.id === "tier-one" ||
      option.id === "tier-two" ||
      option.id === "tier-three" ||
      option.id === "tier-one-two";

    if (isTier) {
      if (isSelected) {
        // Deselecting a tier: remove tier and its countries, keep non-tier countries
        const tierCountries = getTierCountries(option.id);
        const nextIds = selectedIds.filter(
          (id) => id !== option.id && !tierCountries.includes(id)
        );
        setSelected(nextIds);
      } else {
        // Selecting a tier: clear all other tiers and their countries, keep non-tier countries
        const selectedTierIds = getSelectedTierIds();
        const allTierCountriesToRemove: string[] = [];
        
        // Get all countries from all currently selected tiers
        selectedTierIds.forEach((tierId) => {
          const tierCountries = getTierCountries(tierId);
          allTierCountriesToRemove.push(...tierCountries);
        });

        // Get all non-tier countries (countries not in any tier)
        const nonTierCountries = selectedIds.filter(
          (id) => !isTierId(id) && !isCountryInAnyTier(id)
        );

        // Get countries for the new tier being selected
        const newTierCountries = getTierCountries(option.id);

        // Combine: remove all tier IDs and tier countries, keep non-tier countries, add new tier and its countries
        const nextIds = Array.from(
          new Set([
            ...nonTierCountries,
            option.id,
            ...newTierCountries,
          ])
        );
        setSelected(nextIds);
      }
      return;
    }

    // Regular country toggle - just toggle it normally
    const nextIds = isSelected
      ? selectedIds.filter((id) => id !== option.id)
      : [...selectedIds, option.id];

    setSelected(nextIds);
  };

  const handleRemoveChip = (id: string) => {
    // If removing a country that belongs to a selected tier, just remove the country
    // The tier ID will remain selected, but that specific country won't be included
    setSelected(selectedIds.filter((sid) => sid !== id));
  };

  return (
   <div className="w-full">
  <div
    className="relative rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden"
    style={{
      background: "#878DA11A",
      backgroundImage: `url(${ellipseBg})`,
      backgroundSize: "100% 100%",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }}
  >
    {/* HEADER */}
    <button
      type="button"
      className="flex items-center w-full px-6 hover:opacity-90 transition-opacity"
      style={{ height: "88px" }}
      onClick={() => onToggle(id)}
    >
      <div className="flex items-center gap-3 flex-1">
        <img
          src={isOpen ? dropOpenIcon : campaignStepOneIcon}
          alt="Step 6"
          className="w-8 h-8"
        />
        <span
          className="text-[20px] font-normal leading-[130%]"
          style={{
            fontFamily: "Poppins",
            color: isOpen ? "#58F2A9" : "#FFFFFF",
          }}
        >
          Countries
        </span>
      </div>

      <img
        src={downArrow}
        alt="Toggle"
        className="transition-transform"
        style={{
          height: "10px",
          opacity: 0.8,
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        }}
      />
    </button>

    {/* OPEN CONTENT */}
    {isOpen && (
      <div className="px-6 pb-6 flex flex-col gap-[14px]">
        {/* Subtitle */}
        <div className="ml-[44px]">
          <p
            className="text-white"
            style={{
              fontFamily: "Poppins",
              fontSize: "16px",
              fontWeight: 500,
              opacity: 0.9,
            }}
          >
            Choose the target countries where your ad will be shown
          </p>
        </div>

        {/* Selected Chips */}
        {selectedCountryOptions.length > 0 && (
          <div className="flex flex-wrap gap-2 ml-[44px] max-w-[92%]">
            {selectedCountryOptions.map((opt) => (
              <div
                key={opt.id}
                onClick={() => !isEditMode && handleRemoveChip(opt.id)}
                className="inline-flex items-center gap-2 px-3 py-[6px] rounded-full text-white cursor-pointer hover:opacity-90"
                style={{
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  opacity: isEditMode ? 0.6 : 1,
                  cursor: isEditMode ? "not-allowed" : "pointer",
                }}
              >
                <span>{opt.label}</span>
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <path
                    d="M9 3L3 9M3 3L9 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative  mx-w-[92%] ml-[44px] mt-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search Country"
            disabled={isEditMode}
            className="w-full h-[46px] pl-11 pr-4 rounded-[12px] text-white outline-none"
            style={{
              backgroundColor: isEditMode ? "#2A2F3D" : "#1F2733",
              fontFamily: "Poppins",
              opacity: isEditMode ? 0.6 : 1,
              cursor: isEditMode ? "not-allowed" : "text",
            }}
          />
          <img
            src={searchIcon}
            alt="Search"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-80"
          />
        </div>

        {/* Dropdown */}
        <div
          className="ml-[44px] mt-2 rounded-[16px] border border-[#1F2733] max-h-[340px] overflow-y-auto"
          style={{
            backgroundImage: `url(${dropdownBg})`,
            backgroundSize: "100% 100%",
            backgroundColor: "#202027",
          }}
        >
          {filteredOptions.length === 0 && (
            <div className="px-4 py-4 text-white/70 text-sm">
              No countries found
            </div>
          )}

          {filteredOptions.map((option) => {
            const isSelected = selectedIds.includes(option.id);

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => !isEditMode && handleSelect(option)}
                disabled={isEditMode}
                className="w-full flex items-center justify-between px-4 py-[14px] border-b border-[#1F2733] last:border-0"
                style={{
                  opacity: isEditMode ? 0.5 : 1,
                  cursor: isEditMode ? "not-allowed" : "pointer",
                }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                      isSelected
                        ? "border-[#58F2A9] bg-[#58F2A91A]"
                        : "border-[#707070]"
                    }`}
                  >
                    {isSelected && (
                      <span className="h-2.5 w-2.5 rounded-full bg-[#58F2A9]" />
                    )}
                  </span>

                  <span
                    className="text-white"
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "16px",
                    }}
                  >
                    {option.label}
                  </span>
                </div>

                {option.flagEmoji ? (
                  <span className="text-lg">{option.flagEmoji}</span>
                ) : (
                  <img src={globalIcon} alt="" className="w-7 h-7" />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between ml-[44px] mt-2">
          <p
            className="text-white"
            style={{
              fontFamily: "Poppins",
              fontSize: "14px",
              opacity: 0.8,
              margin: 0,
            }}
          >
            Select countries to target your audience
          </p>
          <NextButton size="sm" onClick={() => onNext?.(id)} />
        </div>
      </div>
    )}
  </div>
</div>

  );
};

export default CountriesDropdown;
